import {
  createAsyncThunk,
  createEntityAdapter,
  createSelector,
  createSlice
} from '@reduxjs/toolkit';

// Utils
import { supabase } from 'utils/supabase-client';

import { clearUserData } from './userSlice';

// Thunks
const fetchMenus = createAsyncThunk(
  'menus/fetchMenus',
  async (_, { rejectWithValue }) => {
    try {
      const { data, error } = await supabase.rpc('get_menus');

      if (error) throw error;

      const menusArray = Array.isArray(data?.data) ? data.data : [];

      const filteredMenus = menusArray.map((menu) => {
        const sections = Array.isArray(menu.menuSections) ? menu.menuSections : [];

        return {
          entityId: menu.id,
          menuSections: sections.map(section => ({
            entityId: section.id,
            order: section.order,
            products: Array.isArray(section.menuSectionProducts) ?
              section.menuSectionProducts.map(sectionProduct => ({
                entityId: sectionProduct.product?.entityId,
                sectionProductId: sectionProduct.id
              }))
                .filter(product => !!product.entityId) :
              [],
            translations: section.translations
          }))
        };
      });

      return filteredMenus;
    } catch (error) {
      return rejectWithValue(error?.message);
    }
  }
);

const updateMenu = createAsyncThunk(
  'menus/updateMenu',
  ({ menuId, updatedMenuData, sortedSections }, { rejectWithValue }) => {
    const {
      deletedMenuSections,
      deletedProductsBySectionId,
      menuSections
    } = updatedMenuData || {};

    const menuSectionsAttributes = [];

    if (Array.isArray(menuSections)) {
      /* eslint-disable camelcase */
      menuSections.forEach((section) => {
        const menuSectionProductsAttributes = [];

        if (Array.isArray(section?.products)) {
          section.products.forEach((product, index) => {
            // Add section's products attributes to menuSectionProductsAttributes
            menuSectionProductsAttributes.push({
              id: product.sectionProductId || null,
              order: index,
              product_id: product.entityId
            });
          });
        }

        if (Array.isArray(deletedProductsBySectionId[section.entityId]) && deletedProductsBySectionId[section.entityId].length) {
          deletedProductsBySectionId[section.entityId].forEach((sectionProductToDestroyId) => {
            const sectionProductToDestroy = {
              _destroy: true,
              id: sectionProductToDestroyId
            };

            menuSectionProductsAttributes.push(sectionProductToDestroy);
          });
        }

        const sectionIndex = sortedSections.findIndex(initialFormId => initialFormId === section.initialFormId);

        const sectionAttributesObject = {
          id: section.entityId || null,
          menu_section_products_attributes: menuSectionProductsAttributes,
          menu_sections_to_destroy: section.menuSectionsToDestroy,
          order: sectionIndex + 1 || section.order,
          translations: Object.keys(section.translations || {}).reduce((accumulator, locale) => {
            accumulator[locale] = {
              name: section.translations?.[locale]?.title
            };

            return accumulator;
          }, {})
        };

        menuSectionsAttributes.push(sectionAttributesObject);
      });
    }

    const formattedNewMenuData = {
      menu_sections_attributes: menuSectionsAttributes,
      menu_sections_to_destroy: deletedMenuSections
    };
    /* eslint-enable camelcase */

    return supabase.functions.invoke('update-menu', {
      body: {
        ...formattedNewMenuData,
        id: menuId
      }
    })
      .then(({ data, error }) => {
        const {
          error: functionError,
          data: formattedMenuData
        } = data || {};

        if (error) throw error;

        if (functionError) throw new Error(functionError);

        let filteredMenuData = {};

        if (Array.isArray(formattedMenuData?.menuSections)) {
          filteredMenuData = {
            entityId: formattedMenuData.id,
            menuSections: formattedMenuData.menuSections.map(section => ({
              entityId: section.id,
              order: section.order,
              products: Array.isArray(section.menuSectionProducts) ?
                section.menuSectionProducts.map(sectionProduct => ({
                  entityId: sectionProduct.product.id,
                  sectionProductId: sectionProduct.id
                })) :
                [],
              translations: section.translations
            }))
          };
        }

        return filteredMenuData;
      })
      .catch(error => rejectWithValue(error?.message));
  }
);

// Entity Adapter
const menusAdapter = createEntityAdapter({
  selectId: menu => menu.entityId
});

// Additional Initial State Options
const additionalInitialStateOptions = {
  isFetchingMenus: false,
  isUpdatingMenus: false
};

const menusSlice = createSlice({
  extraReducers: (builder) => {
    builder
      // Fetch Menus Handlers
      .addCase(fetchMenus.pending, (state) => {
        state.isFetchingMenus = true;
      })
      .addCase(fetchMenus.fulfilled, (state, { payload }) => {
        state.isFetchingMenus = false;

        const menus = payload;

        if (menus) {
          menusAdapter.setAll(state, menus);
        }
      })
      .addCase(fetchMenus.rejected, (state) => {
        state.isFetchingMenus = false;
      })
      // Handle Logout
      .addCase(clearUserData, () => ({
        ...menusAdapter.getInitialState()
      }))
      // Update Menu Handlers
      .addCase(updateMenu.pending, (state) => {
        state.isUpdatingMenus = true;
      })
      .addCase(updateMenu.fulfilled, (state, { payload }) => {
        state.isUpdatingMenus = false;

        const menu = payload;

        if (menu) {
          menusAdapter.upsertOne(state, menu);
        }
      })
      .addCase(updateMenu.rejected, (state) => {
        state.isUpdatingMenus = false;
      });
  },

  initialState: menusAdapter.getInitialState(additionalInitialStateOptions),

  name: 'menus'
});

// Selectors
const menusSelectors = menusAdapter.getSelectors(state => state.menus);

const { selectAll: selectMenus } = menusSelectors;

const selectCurrentMenu = createSelector(
  selectMenus,
  menus => menus[0]
);

const selectIsFetchingMenus = state => state.menus.isFetchingMenus;

const selectIsUpdatingMenus = state => state.menus.isUpdatingMenus;

export {
  fetchMenus,
  selectCurrentMenu,
  selectIsFetchingMenus,
  selectIsUpdatingMenus,
  selectMenus,
  updateMenu
};

export default menusSlice.reducer;
